import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Plus, Search, Unlink } from 'lucide-react'
import { me as getMe } from '../../api/auth'
import { clientFigures, dropClient, emailOwnerAgain, listClients, openClient } from '../../api/practice'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Spinner from '../../components/Spinner'
import { StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { PracticeTabs, clientMoney, initials, logoSrc, monthName } from './practiceShared'

const FIGURES_HELP = 'From each client’s books today: cash and bank, what customers owe them, what they owe vendors, and the profit so far this month. Below them, what’s waiting: last month, if it has entries and isn’t closed yet; draft journal entries, vendor invoices still to check, approve or post, and bank lines not matched.'

/** What's waiting in a client's books, as chips. */
function Waiting({ f }) {
  const chips = []
  if (!f.last_closed && f.last_has_entries) chips.push({ label: `${monthName(f.last_month)} not closed`, tone: 'warn' })
  if (f.drafts) chips.push({ label: `${f.drafts} draft ${f.drafts === 1 ? 'entry' : 'entries'}`, tone: 'info' })
  if (f.invoices_waiting) chips.push({ label: `${f.invoices_waiting} vendor ${f.invoices_waiting === 1 ? 'invoice' : 'invoices'}`, tone: 'info' })
  if (f.bank_unmatched) chips.push({ label: `${f.bank_unmatched} bank ${f.bank_unmatched === 1 ? 'line' : 'lines'} to match`, tone: 'info' })
  if (!chips.length) chips.push({ label: 'Up to date', tone: 'good' })
  return <div className="pr-waiting">{chips.map((c) => <StatusBadge key={c.label} label={c.label} tone={c.tone} />)}</div>
}

function ClientCard({ c, admin, opening, onOpen, onDrop }) {
  const { notify } = useToast()
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const { data: f, error } = useQuery({
    queryKey: ['practice-figures', c.client_id],
    queryFn: () => clientFigures(c.client_id),
    enabled: !c.suspended,
    staleTime: 60_000,
  })
  const again = async () => {
    setSending(true); setSendError('')
    try {
      const r = await emailOwnerAgain(c.client_id)
      notify(r.email_sent ? `Link emailed to ${r.email}` : 'Couldn’t send the email')
      if (!r.email_sent) setSendError('The email didn’t send. The owner can use “Forgot password” on the sign-in page, with their Company ID and email.')
    } catch (e) { setSendError(e.message || 'Couldn’t send the link.') }
    setSending(false)
  }
  const fig = (label, value) => (
    <div className="pr-fig">
      <span>{label}</span>
      <strong className={value < 0 ? 'neg' : ''}>{f ? clientMoney(value, c.currency) : '…'}</strong>
    </div>
  )
  return (
    <div className="pr-client">
      <div className="pr-client-head">
        {c.logo_path ? <img className="pr-logo" src={logoSrc(c.logo_path)} alt="" /> : <span className="pr-logo pr-logo-text">{initials(c.name)}</span>}
        <div className="pr-client-name">
          <strong>{c.name}</strong>
          <span className="cell-sub">{c.plan} · {c.currency}{c.since ? ` · since ${fmtDay(c.since)}` : ''}</span>
        </div>
      </div>
      {!c.suspended && (error ? <p className="doc-hint">Couldn’t read the figures. Open the books to see them.</p> : (
        <>
          <div className="pr-figs">
            {fig('Cash', f?.cash)}
            {fig('Owed to them', f?.owed_to_you)}
            {fig('They owe vendors', f?.owed_to_vendors)}
            {fig(f ? `Profit, ${monthName(f.month)}` : 'Profit this month', f?.profit_this_month)}
          </div>
          {f && <Waiting f={f} />}
        </>
      ))}
      {admin && c.owner_waiting && (
        <div className="pr-owner">
          <span className="cell-sub">The owner hasn’t chosen a password yet.</span>
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={sending} onClick={again}>{sending ? 'Sending…' : 'Email the link again'}</button>
          {sendError && <span className="acc-field-error">{sendError}</span>}
        </div>
      )}
      <div className="pr-client-foot">
        {c.suspended ? <StatusBadge label="Suspended" tone="bad" /> : <StatusBadge label={c.access_label} tone={c.access === 'full' ? 'good' : 'info'} />}
        <div className="pr-client-actions">
          {admin && (
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm pr-drop" disabled={opening !== null} onClick={() => onDrop(c)}
              title="Stop looking after this client" aria-label={`Stop looking after ${c.name}`}><Unlink size={14} /></button>
          )}
          <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" disabled={c.suspended || opening !== null} onClick={() => onOpen(c)}>
            {opening === c.client_id ? 'Opening…' : <>Open books<ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  )
}

/** A practice's home: the companies it looks after, with figures from each
    one's books, each opened into its books. */
export default function Clients({ user, onSwitched }) {
  const nav = useNavigate()
  const { state } = useLocation()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [opening, setOpening] = useState(null)
  const [search, setSearch] = useState('')
  const [dropped, setDropped] = useState(null)

  useEffect(() => {
    listClients().then(setData).catch((e) => { setError(e.message || 'Couldn’t load your clients.'); setData({ clients: [] }) })
  }, [])

  const open = async (c) => {
    setOpening(c.client_id); setError('')
    try {
      await openClient(c.client_id)
      onSwitched && onSwitched(await getMe())
      nav('/accounting', { replace: true })
    } catch (e) {
      setError(e.message || `Couldn’t open ${c.name}.`)
      setOpening(null)
    }
  }

  // Stop looking after a client (admins): the company keeps its books and is told.
  const drop = async (c) => {
    if (!window.confirm(`Stop looking after ${c.name}?\n\nEveryone in your practice loses access to its books at once. ${c.name} keeps its account and books, we let its owner know, and they can invite you again.`)) return
    setError('')
    try {
      const r = await dropClient(c.client_id)
      setData((d) => ({ ...d, clients: d.clients.filter((x) => x.client_id !== c.client_id) }))
      setDropped({ name: c.name, told: r.owner_told })
    } catch (e) { setError(e.message || `Couldn’t stop looking after ${c.name}.`) }
  }

  const clients = data?.clients || []
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return term ? clients.filter((c) => c.name.toLowerCase().includes(term)) : clients
  }, [clients, search])
  const firm = data?.practice?.name || ''
  const admin = data?.admin === true
  const added = state?.added
  return (
    <div className="ci-page">
      <PracticeTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Clients <HelpTip text={FIGURES_HELP} /></h1>
            <div className="ci-title-meta">
              The companies {firm || 'your practice'} looks after. Open one to work in its books.
              {user?.tenant_id ? ` Your practice’s Company ID is ${user.tenant_id}.` : ''}
            </div>
          </div>
        </div>
        {admin && (
          <div className="ci-header-actions">
            <Link className="ci-btn ci-btn-primary" to="/practice/clients/new"><Plus size={15} />Add a client</Link>
          </div>
        )}
      </div>
      {(state?.welcome || state?.accepted) && (
        <p className="pr-note">
          {state.welcome && `Your practice is ready. Its Company ID is ${state.welcome}: note it down, you and your staff sign in with it. `}
          {state.accepted && `${state.accepted} is now one of your clients.`}
        </p>
      )}
      {added && (
        <p className="pr-note">
          {added.name} is ready, with Full books for your practice. {added.email_sent
            ? `We emailed ${added.owner_email} a link to choose a password; they sign in with Company ID ${added.client_id} and username ${added.username}.`
            : `We couldn’t email ${added.owner_email}. Tell them to sign in with Company ID ${added.client_id}: “Forgot password” on the sign-in page, with that email, lets them choose a password.`}
        </p>
      )}
      {dropped && <p className="pr-note">You no longer look after {dropped.name}.{dropped.told ? ' We emailed its owner.' : ''}</p>}
      {(state?.notice || user?.notice) && <ErrorBanner message={state?.notice || user.notice} />}
      {error && <ErrorBanner message={error} />}

      {clients.length > 6 && (
        <div className="ci-toolbar">
          <label className="ci-search" aria-label="Search clients">
            <Search size={14} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Company name…" />
          </label>
        </div>
      )}

      {data === null ? <Spinner label="Loading your clients…" /> : clients.length === 0 ? (
        <div className="ci-form-meta-card pr-empty">
          <div className="mf-card-title">No clients yet</div>
          {!admin ? (
            <p className="doc-hint">Your practice’s admin chooses which clients you can open. Ask them to give you some.</p>
          ) : (
            <p className="doc-hint">
              <strong>Add a client</strong> to set up ASYS for a company you look after: they get a free trial and
              pay for their own plan. Or ask a client already on ASYS to invite you: <strong>Settings → Accountant</strong>,
              with your email. Either way you see their books, even if their plan doesn’t include Accounting.
            </p>
          )}
        </div>
      ) : shown.length === 0 ? (
        <div className="ci-empty">No client matches “{search}”.</div>
      ) : (
        <div className="pr-clients">
          {shown.map((c) => <ClientCard key={c.client_id} c={c} admin={admin} opening={opening} onOpen={open} onDrop={drop} />)}
        </div>
      )}
    </div>
  )
}
